import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getPipeline, movePipelineLead } from '../../../../Services/CRMAdminServices/CRMDashboardService';
import { getCrmDDLService } from '../../../../Services/CRMAdminServices/CRMTaskListService';
import { formatMobile } from '../../../common/MobileField';
import { isMbStageName } from '../../../../Utils/leadStageType';

/* One lead, as it sits on the board. */
interface Card {
    leadID: number;
    name: string;
    mobile: string;
    email: string;
    source: string;
    staff: string;
    followUp: string;
    daysInStage: number;
}
interface Column {
    name: string;
    count: number;
    /* Everyone who ever reached this stage, not only those sitting on it now. It is what
       the conversion figure is measured on - a lead that passed through still counts. */
    reached: number;
    pct: string;
    cards: Card[];
}

/* The follow-up date arrives as text in whatever shape it was saved: 2026-10-05,
   2026-10-04T13:00:00.000Z, 05/10/2026 or 5/10/2026. Read the Australian way, day first.
   A date that carries a time zone is turned into the local day before anything else. */
export const parseFollowUp = (v: string): Date | null => {
    const t = (v || '').trim();
    if (!t) return null;
    if (/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/i.test(t)) {
        const d = new Date(t);
        return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return null;
};
/* DD/MM/YYYY, always. */
export const showFollowUp = (d: Date): string =>
    `${('0' + d.getDate()).slice(-2)}/${('0' + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
/* Overdue means the date is before today. Today itself is not overdue. */
export const isOverdue = (d: Date | null, now: Date = new Date()): boolean =>
    !!d && d.getTime() < new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

/* WHO FOLLOWS UP.  Richard, 10 Oct 2026: the tile says "Sonia texts 14/10/2026" on her
   stages and "Followup 14/10/2026" on his. Sonia works a client up to Gameplan Completed
   and a broker on MB - Enquiry; from the Discovery Call on the date is Richard's own.
   Matched on the stage name with spaces and dashes taken out. A stage that is renamed
   or added reads "Followup" until it is listed here, which is the safe way to be wrong. */
const SONIA_STAGES = ['enquiry', 'gameplanlink', 'gameplanstarted', 'gameplancompleted', 'mbenquiry'];
export const isSoniaStage = (name: string): boolean =>
    SONIA_STAGES.includes((name || '').replace(/[\s-]/g, '').toLowerCase());

/* Chevron colours, left to right. The board reads cold to warm, so where a lead
   has got to is legible before any of the words are. */
const TINTS = ['#4b4ddb', '#3f6fe0', '#2f93e0', '#22a8c4', '#18b39a', '#13ac6e', '#1aa34a', '#2f9a2f', '#4f9a1f'];

const PipelineBoard: React.FC<{ which?: 'property' | 'mb' }> = ({ which = 'property' }) => {
    const [cols, setCols] = useState<Column[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dragId, setDragId] = useState<number | null>(null);
    const [overCol, setOverCol] = useState<string | null>(null);
    /* The lead carries a staff id, not a name. The CRM's own dropdown list is
       where the names live, so it is read once and used as a lookup. */
    const [staffNames, setStaffNames] = useState<Record<string, string>>({});

    const load = useCallback(async () => {
        try {
            const r: any = await getPipeline(200);
            const d = r?.data?.data ?? r?.data?.Data ?? r?.data;
            /* One board per business. The stage name says which it belongs to,
               the same rule the task lists and the dashboard already use. */
            const all: Column[] = d?.columns ?? d?.Columns ?? [];
            setCols(all.filter(c => isMbStageName(c.name) === (which === 'mb')));
            setTotal(d?.leadsTotal ?? d?.LeadsTotal ?? 0);
            setError('');
        } catch (e: any) {
            setError(e?.message || 'Could not load the pipeline.');
        } finally {
            setLoading(false);
        }
    }, [which]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const r: any = await getCrmDDLService();
                const list = r?.data?.data?.staff ?? r?.data?.staff ?? [];
                const map: Record<string, string> = {};
                (list as any[]).forEach(x => { if (x?.value != null) map[String(x.value)] = x.label ?? ''; });
                setStaffNames(map);
            } catch { /* no names - the card just shows the days */ }
        })();
    }, []);

    /* Move the card on screen first, then tell the server. If the server says no,
       reload and the board snaps back to the truth. */
    const drop = async (toStage: string) => {
        const id = dragId;
        setOverCol(null);
        setDragId(null);
        if (!id) return;

        const from = cols.find(c => c.cards.some(x => x.leadID === id));
        if (!from || from.name === toStage) return;
        const card = from.cards.find(x => x.leadID === id)!;

        setCols(prev => prev.map(c => {
            if (c.name === from.name) return { ...c, count: c.count - 1, cards: c.cards.filter(x => x.leadID !== id) };
            if (c.name === toStage) return { ...c, count: c.count + 1, cards: [{ ...card, daysInStage: 0 }, ...c.cards] };
            return c;
        }));

        try {
            const r: any = await movePipelineLead(id, toStage);
            const ok = r?.data?.success ?? r?.data?.Success;
            if (!ok) { toast.error(r?.data?.message || 'That move did not save.'); load(); }
            else toast.success(`${card.name} moved to ${toStage}.`);
        } catch {
            toast.error('Could not save that move.');
            load();
        }
    };

    if (loading) return <div className='p-4'>Loading the pipeline…</div>;
    if (error) return <div className='p-4' style={{ color: '#b32020' }}>{error}</div>;
    if (!cols.length) return <div className='p-4'>No stages to show.</div>;

    return (
        <div>
            <div className='d-flex align-items-baseline mb-2' style={{ gap: '10px' }}>
                <h3 className='f-w-900 mb-0' style={{ color: 'blue' }}>Pipeline</h3>
                <span style={{ color: '#767d95', fontSize: '13px' }}>{total} leads &middot; drag a card to move it</span>
            </div>

            {/* One row, scrolled sideways. Columns keep their width so a long pipeline
                stays readable instead of squeezing every card to nothing. */}
            <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', minWidth: 'min-content' }}>
                    {cols.map((c, i) => {
                        const tint = TINTS[i % TINTS.length];
                        const over = overCol === c.name;
                        const sonia = isSoniaStage(c.name);
                        return (
                            <div
                                key={c.name}
                                onDragOver={(e) => { e.preventDefault(); setOverCol(c.name); }}
                                onDragLeave={() => setOverCol(o => (o === c.name ? null : o))}
                                onDrop={(e) => { e.preventDefault(); drop(c.name); }}
                                style={{ flex: '0 0 250px', width: '250px' }}
                            >
                                {/* the chevron */}
                                <div style={{
                                    background: tint, color: '#fff', fontWeight: 700, fontSize: '13px',
                                    padding: '8px 12px', position: 'relative',
                                    clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)'
                                }}>
                                    {/*  The conversion that used to sit in a second row of chevrons above
                                         the board. It belongs here, on the stage it describes, where it
                                         cannot disagree with the stage name beside it. Measured against
                                         the stage before, on everyone who ever reached each one.  */}
                                    {(() => {
                                        //  People skip stages - a lead can land on Gameplan Completed
                                        //  without ever being flagged Started - so the stage immediately
                                        //  before can be smaller than this one and the figure reads over
                                        //  100%. Measure against the largest stage before it instead.
                                        const val = (x: Column) => x.reached || x.count;
                                        const prev = i === 0 ? 0 : Math.max(...cols.slice(0, i).map(val));
                                        const here = val(c);
                                        const conv = i === 0 || prev <= 0 ? null : (here / prev) * 100;
                                        const label = conv === null ? '—' : `${conv.toFixed(conv < 10 ? 1 : 0)}%`;
                                        return (
                                            <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, paddingLeft: '8px' }}>
                                                <span>{c.name} / {c.count}</span>
                                                <span style={{ fontWeight: 600, opacity: .85, fontSize: '12px' }}
                                                      title='Conversion against the largest stage before it, on everyone who ever reached each one'>
                                                    {label}
                                                </span>
                                            </span>
                                        );
                                    })()}
                                </div>

                                <div style={{
                                    background: over ? '#eef4ff' : '#f6f7fb',
                                    border: over ? '2px dashed ' + tint : '1px solid #e6e8f0',
                                    borderTop: 'none', borderRadius: '0 0 6px 6px',
                                    minHeight: '320px', maxHeight: '62vh', overflowY: 'auto', padding: '8px'
                                }}>
                                    {c.cards.length === 0 && (
                                        <div style={{ color: '#9aa1b5', fontSize: '12.5px', padding: '10px 4px' }}>Nobody here.</div>
                                    )}
                                    {c.cards.map(card => {
                                        const follow = parseFollowUp(card.followUp);
                                        const overdue = isOverdue(follow);
                                        return (
                                        <div
                                            key={card.leadID}
                                            draggable
                                            onDragStart={() => setDragId(card.leadID)}
                                            onDragEnd={() => { setDragId(null); setOverCol(null); }}
                                            style={{
                                                /* A follow-up date that has passed turns the whole tile red. */
                                                background: overdue ? '#ffd9d9' : '#fff',
                                                border: overdue ? '1px solid #d92d2d' : '1px solid #e6e8f0', borderRadius: '6px',
                                                padding: '10px 11px', marginBottom: '8px', cursor: 'grab',
                                                opacity: dragId === card.leadID ? 0.45 : 1
                                            }}
                                        >
                                            {/* The lead number first, because that is what staff quote to
                                                each other. Only this line opens the lead - the mobile dials
                                                and the email is there to read, not to click by accident. */}
                                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                                                <a
                                                    href={`${process.env.PUBLIC_URL}/EditLead/${card.leadID}`}
                                                    style={{ fontWeight: 800, fontSize: '12px', color: '#0033cc', textDecoration: 'none' }}
                                                >Lead {card.leadID}</a>
                                                <span style={{ fontSize: '11.5px', color: card.daysInStage >= 14 ? '#b32020' : '#767d95', whiteSpace: 'nowrap' }}>
                                                    {card.daysInStage} {card.daysInStage === 1 ? 'day' : 'days'} here
                                                </span>
                                            </div>

                                            {/* The name opens the lead as well as the number above it -
                                                the name is what anyone looking at the board reads first. */}
                                            <div style={{ marginTop: '3px' }}>
                                                <a
                                                    href={`${process.env.PUBLIC_URL}/EditLead/${card.leadID}`}
                                                    style={{ fontWeight: 700, fontSize: '13.5px', color: '#111528', textDecoration: 'none' }}
                                                >{card.name}</a>
                                            </div>

                                            <div style={{ fontSize: '12px', color: '#464c63', marginTop: '4px', lineHeight: 1.5 }}>
                                                {card.mobile && <div><a href={`tel:${card.mobile}`} style={{ color: '#464c63' }}>{formatMobile(card.mobile)}</a></div>}
                                                {card.email && <div style={{ wordBreak: 'break-all' }}>{card.email}</div>}
                                            </div>

                                            <div style={{ fontSize: '11.5px', color: overdue ? '#7a1414' : '#767d95', marginTop: '6px' }}>
                                                {staffNames[String(card.staff)] || 'Unassigned'}
                                            </div>
                                            {follow && (
                                                <div style={{ fontSize: '11.5px', color: overdue ? '#b32020' : '#767d95', fontWeight: overdue ? 700 : 400, marginTop: '2px' }}>
                                                    {sonia ? 'Sonia texts' : 'Followup'} {showFollowUp(follow)}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default PipelineBoard;
